import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TIERS = {
  starter: {
    name: "Starter",
    monthlyPrice: 50,
    annualPrice: 500,
    description: "Perfect for new entrepreneurs",
    features: ["1 business", "5 Opturna accounts", "Basic allocation tracking", "Email reminders", "Deductions library", "Basic reports"]
  },
  pro: {
    name: "Pro",
    monthlyPrice: 99,
    annualPrice: 990,
    description: "For growing businesses ready to scale",
    features: ["3 businesses", "5 Opturna accounts", "Bank connections (Plaid)", "PDF & CSV exports", "Tax planning tools", "Email & SMS reminders", "CPA email reports", "Financial health score"]
  },
  enterprise: {
    name: "Enterprise",
    monthlyPrice: 149,
    annualPrice: 1490,
    description: "Full power for established businesses",
    features: ["Unlimited businesses", "5 Opturna accounts", "Bank connections (Plaid)", "PDF & CSV exports", "Tax planning tools", "Email & SMS reminders", "CPA email reports", "Financial health score", "Priority support", "Custom reports"]
  },
  wealth_builder: {
    name: "Wealth Builder",
    monthlyPrice: 249,
    annualPrice: 2490,
    description: "Tax App + Wealth Engine for serious builders",
    features: ["Everything in Enterprise", "Wealth Engine", "Available to Deploy calc", "Goal tracking & contributions", "Allocation suggestions", "Insights Engine", "Activate This Step actions", "Dedicated onboarding"]
  }
};

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [polling, setPolling] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [pilotCode, setPilotCode] = useState("");
  const [redeemingCode, setRedeemingCode] = useState(false);
  const { fetchSubscription: refreshAuth } = useAuth();

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      fetchSubscription();
    }
  }, [sessionId]);

  const fetchSubscription = async () => {
    try {
      const response = await api.get("/subscription/current");
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemPilot = async () => {
    if (!pilotCode.trim()) return;
    setRedeemingCode(true);
    try {
      const res = await api.post("/subscription/redeem-pilot", { code: pilotCode.trim() });
      // Fire Meta Pixel StartTrial event
      if (window.fbq) window.fbq('track', 'StartTrial', { value: 0, currency: 'USD', predicted_ltv: 249 });
      toast.success(res.data.message);
      setPilotCode("");
      fetchSubscription();
      if (refreshAuth) refreshAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pilot code");
    } finally {
      setRedeemingCode(false);
    }
  };

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please contact support.");
      setLoading(false);
      return;
    }

    setPolling(true);
    try {
      const response = await api.get(`/subscription/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("Payment successful! Welcome to Opturna Tax AI!");
        setPolling(false);
        await fetchSubscription();
        navigate("/subscription", { replace: true });
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setPolling(false);
        setLoading(false);
        navigate("/subscription", { replace: true });
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const handleSubscribe = async (tier) => {
    setProcessing(tier);
    try {
      const originUrl = window.location.origin;
      const response = await api.post("/subscription/checkout", { 
        origin_url: originUrl, 
        tier,
        billing_period: isAnnual ? "annual" : "monthly"
      });
      
      if (response.data.checkout_url) {
        // Fire Meta Pixel StartTrial event on checkout
        const tierInfo = TIERS[tier];
        if (window.fbq) window.fbq('track', 'StartTrial', { value: tierInfo?.monthlyPrice || 0, currency: 'USD' });
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const calculateSavings = (monthly, annual) => (monthly * 12) - annual;

  if (loading || polling) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-12 h-12 border-4 border-[#1e3a5f]/20 border-t-[#1e3a5f] rounded-full animate-spin" />
        <p className="text-muted-foreground">
          {polling ? "Verifying your payment..." : "Loading subscription..."}
        </p>
      </div>
    );
  }

  // Active subscription view
  if (subscription?.is_active) {
    const tierInfo = TIERS[subscription.tier] || TIERS.starter;
    const isAnnualSub = subscription.billing_period === "annual";
    
    return (
      <div className="max-w-2xl mx-auto space-y-8" data-testid="subscription-page">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-stone-900">Subscription</h1>
          <p className="text-stone-600">Manage your Opturna Tax AI&#8482; subscription</p>
        </div>

        <Card className="border-[#1e3a5f]/20 bg-gradient-to-br from-[#1e3a5f]/5 to-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Opturna Tax AI&#8482; {tierInfo.name}</CardTitle>
              <Badge className={subscription.status === "trialing" ? "bg-blue-600" : "bg-[#1e3a5f]"}>
                {subscription.status === "trialing" ? "Trial" : "Active"}
              </Badge>
            </div>
            <CardDescription>{tierInfo.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-white border">
                <p className="text-sm text-stone-500 mb-1">Plan</p>
                <p className="font-semibold text-stone-900">{tierInfo.name} ({isAnnualSub ? 'Annual' : 'Monthly'})</p>
              </div>
              <div className="p-4 rounded-lg bg-white border">
                <p className="text-sm text-stone-500 mb-1">Price</p>
                <p className="font-semibold text-stone-900">${subscription.amount}/{isAnnualSub ? 'year' : 'month'}</p>
              </div>
              {subscription.status === "trialing" && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 col-span-2">
                  <p className="text-sm text-blue-600 mb-1">Trial Ends</p>
                  <p className="font-semibold text-blue-800">{formatDate(subscription.trial_end)}</p>
                </div>
              )}
              {isAnnualSub && subscription.annual_savings > 0 && (
                <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 col-span-2">
                  <p className="text-sm text-[#1e3a5f] mb-1">Annual Savings</p>
                  <p className="font-semibold text-[#1e3a5f]">${subscription.annual_savings}/year</p>
                </div>
              )}
              <div className="p-4 rounded-lg bg-white border col-span-2">
                <p className="text-sm text-stone-500 mb-1">Next Billing Date</p>
                <p className="font-semibold text-stone-900">{formatDate(subscription.current_period_end)}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-semibold text-stone-900 mb-3">Your Features</h4>
              <div className="grid grid-cols-2 gap-2">
                {tierInfo.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-stone-600">
                    <svg className="w-4 h-4 text-[#1e3a5f]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pricing view for non-subscribers
  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="subscription-page">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-stone-900">Choose Your Plan</h1>
        <p className="text-stone-600 mb-6">Start with a 7-day free trial. Cancel anytime.</p>
        
        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-stone-900' : 'text-stone-500'}`}>Monthly</span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            className="data-[state=checked]:bg-[#1e3a5f]"
          />
          <span className={`text-sm font-medium ${isAnnual ? 'text-stone-900' : 'text-stone-500'}`}>Annual</span>
          <Badge className="bg-[#1e3a5f] text-white">Save 2 months</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(TIERS).map(([key, tier]) => {
          const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
          const savings = calculateSavings(tier.monthlyPrice, tier.annualPrice);
          
          return (
            <Card 
              key={key} 
              className={`relative ${key === 'pro' ? 'border-2 border-[#1e3a5f] shadow-xl' : key === 'wealth_builder' ? 'border-2 border-emerald-500 shadow-xl' : 'border-stone-200'}`}
            >
              {key === 'pro' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#1e3a5f]">Most Popular</Badge>
                </div>
              )}
              {key === 'enterprise' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-stone-600">Best Value</Badge>
                </div>
              )}
              {key === 'wealth_builder' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-600">Upgrade</Badge>
                </div>
              )}
              <CardContent className="pt-8 pb-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-stone-900 mb-1">{tier.name}</h3>
                  <p className="text-stone-500 text-sm mb-4">{tier.description}</p>
                  <div>
                    <span className="text-4xl font-bold text-stone-900">${price}</span>
                    <span className="text-stone-500">/{isAnnual ? 'year' : 'mo'}</span>
                  </div>
                  {isAnnual && (
                    <p className="text-sm text-[#1e3a5f] font-medium mt-1">Save ${savings}/year</p>
                  )}
                </div>

                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-[#1e3a5f] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-stone-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(key)}
                  disabled={processing !== null}
                  className={`w-full py-5 rounded-full ${
                    key === 'pro' 
                      ? 'bg-[#1e3a5f] hover:bg-[#2d5a8e] text-white' 
                      : key === 'wealth_builder'
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-stone-100 hover:bg-stone-200 text-stone-900'
                  }`}
                  data-testid={`subscribe-${key}-btn`}
                >
                  {processing === key ? "Processing..." : "Start Free Trial"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center p-6 rounded-xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
        <p className="text-[#1e3a5f]">
          <strong>7-day free trial</strong> on all plans. {isAnnual && <span className="text-[#1e3a5f]">Annual billing saves you 2 months!</span>}
        </p>
      </div>

      {/* Pilot Code Redemption */}
      <Card className="border-dashed border-2 border-emerald-200" data-testid="pilot-code-section">
        <CardContent className="py-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-stone-900 text-sm">Have a pilot code?</p>
              <p className="text-xs text-stone-500">Enter your invite code to unlock free access</p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Input
                placeholder="OPTURNA-PILOT-XXXXXXXX"
                value={pilotCode}
                onChange={(e) => setPilotCode(e.target.value.toUpperCase())}
                className="font-mono text-sm w-full sm:w-64"
                data-testid="pilot-code-input"
              />
              <Button
                onClick={handleRedeemPilot}
                disabled={redeemingCode || !pilotCode.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 flex-shrink-0"
                data-testid="redeem-pilot-btn"
              >
                {redeemingCode ? "Redeeming..." : "Redeem"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
